import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import { Footer } from "./components/CTAFooter";

// Brand-styled 404 page for the React Router catch-all route.
// Wire it up in App.js like this:
//
//   import NotFound from "./NotFound";
//   ...
//   <Routes>
//     <Route path="/" element={<Home />} />
//     ...all your other real routes...
//     <Route path="*" element={<NotFound />} />
//   </Routes>
//
// The "*" route MUST be the last one in the list - React Router only falls
// through to it when nothing above matches.

const REDIRECT_SECONDS = 8;

export default function NotFound() {
  const navigate = useNavigate();
  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);
  const [cancelled, setCancelled] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    document.title = "Page Not Found | AbhinavJoshi";

    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (cancelled) return undefined;
    if (seconds <= 0) {
      navigate("/");
      return undefined;
    }
    timerRef.current = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [seconds, cancelled, navigate]);

  const handleCancel = () => {
    setCancelled(true);
    clearTimeout(timerRef.current);
  };

  return (
    <div className="min-h-screen bg-white font-sans text-neutral-900">
      <Navbar />
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        <div
        style={{
          background: "#ffffff",
          border: "1px solid #e6e8eb",
          borderRadius: "20px",
          maxWidth: "520px",
          width: "100%",
          padding: "56px 44px 44px",
          textAlign: "center",
          boxShadow:
            "0 1px 2px rgba(0,0,0,0.03), 0 20px 50px -20px rgba(0,13,20,0.15)",
        }}
      >
        <span
          style={{
            fontFamily: "'Roboto Mono', monospace",
            fontSize: "12px",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#5b6570",
            display: "inline-block",
            padding: "6px 14px",
            border: "1px solid #e6e8eb",
            borderRadius: "999px",
            marginBottom: "28px",
          }}
        >
          Error 404
        </span>

        <div
          style={{
            fontSize: "88px",
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: "-0.03em",
            margin: "0 0 14px",
            background: "linear-gradient(135deg, #000d14 0%, #2a5570 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          404
        </div>

        <h1
          style={{
            fontSize: "23px",
            fontWeight: 700,
            margin: "0 0 12px",
            letterSpacing: "-0.01em",
            color: "#000d14",
          }}
        >
          This page doesn't exist
        </h1>

        <p
          style={{
            fontSize: "15px",
            lineHeight: 1.6,
            color: "#5b6570",
            margin: "0 0 32px",
          }}
        >
          The link you followed may be outdated or broken. It's not something
          you need to worry about, we'll get you back on track.
        </p>

        {!cancelled ? (
          <p style={{ fontSize: "14px", color: "#5b6570", margin: "0 0 28px" }}>
            You're redirecting to the main page in{" "}
            <strong style={{ color: "#000d14" }}>{seconds}</strong> seconds
          </p>
        ) : (
          <p style={{ fontSize: "14px", color: "#5b6570", margin: "0 0 28px" }}>
            Automatic redirect cancelled.
          </p>
        )}

        <div
          style={{
            width: "100%",
            height: "3px",
            background: "#e6e8eb",
            borderRadius: "99px",
            overflow: "hidden",
            marginBottom: "32px",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${(seconds / REDIRECT_SECONDS) * 100}%`,
              background: "#000d14",
              borderRadius: "99px",
              transition: "width 1s linear",
            }}
          />
        </div>

        <div style={{ position: "relative", display: "inline-block" }}>
          <div
            style={{
              position: "absolute",
              inset: "-8px",
              background:
                "radial-gradient(circle, rgba(0,60,90,0.35) 0%, transparent 70%)",
              filter: "blur(10px)",
              borderRadius: "20px",
              zIndex: 0,
            }}
          />
          <button
            type="button"
            onClick={() => navigate("/")}
            style={{
              position: "relative",
              zIndex: 1,
              display: "inline-block",
              background: "#000d14",
              color: "#fff",
              fontSize: "14px",
              fontWeight: 600,
              padding: "13px 28px",
              borderRadius: "12px",
              border: "none",
              cursor: "pointer",
            }}
          >
            Go to homepage now
          </button>
        </div>

        {!cancelled && (
          <button
            type="button"
            onClick={handleCancel}
            style={{
              display: "block",
              margin: "18px auto 0",
              fontSize: "13px",
              color: "#5b6570",
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Stay on this page
          </button>
        )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
